import { api } from './api';
import { Categoria } from '../types';

/**
 * Request interface for creating/updating categories
 * Maps frontend fields to backend expected fields
 */
export interface CategoriaRequest {
  nome: string;
  tipo: 'ENTRATA' | 'USCITA' | 'ENTRAMBI';
  descrizione?: string;
  colore?: string;
  icona?: string;
  isActive?: boolean;
  parentId?: number;
}

/**
 * Filters for category queries
 */
export interface CategoriaFilters {
  tipo?: 'ENTRATA' | 'USCITA';
  isActive?: boolean;
}

/**
 * Backend response category format
 */
interface CategoriaBackend {
  id: number;
  nome: string;
  tipo: 'ENTRATA' | 'USCITA' | 'ENTRAMBI';
  descrizione?: string;
  colore?: string;
  icona?: string;
  isActive: boolean;
  parentId?: number;
  createdAt: string;
  updatedAt: string;
  children?: CategoriaBackend[];
}

/**
 * Map backend tipo to frontend tipo
 */
const mapTipo = (tipo?: string): 'entrata' | 'uscita' | 'entrambi' => {
  if (!tipo) return 'entrambi';
  const tipoUpper = tipo.toUpperCase();
  if (tipoUpper === 'ENTRATA') return 'entrata';
  if (tipoUpper === 'USCITA') return 'uscita';
  if (tipoUpper === 'ENTRAMBI') return 'entrambi';
  return 'entrambi';
};

/**
 * Transform backend category to frontend format
 */
const mapBackendToFrontend = (backend: CategoriaBackend): Categoria => {
  return {
    id: backend.id,
    nome: backend.nome,
    tipo: mapTipo(backend.tipo),
    colore: backend.colore,
    icona: backend.icona,
    descrizione: backend.descrizione,
    attiva: backend.isActive ?? true,
    utente_id: 0,
    parentId: backend.parentId,
  };
};

/**
 * Flatten hierarchical categories into a flat list
 * Backend returns categories with nested children, frontend expects flat list with parentId
 * Children inherit tipo and isActive from parent if not set
 */
const flattenCategories = (categories: CategoriaBackend[]): CategoriaBackend[] => {
  const result: CategoriaBackend[] = [];

  for (const cat of categories) {
    // Add the parent category
    result.push(cat);

    // Add children as separate items, inheriting tipo from parent if not set
    if (cat.children && cat.children.length > 0) {
      for (const child of cat.children) {
        result.push({
          ...child,
          parentId: cat.id,
          tipo: child.tipo || cat.tipo, // Inherit tipo from parent if not set
          isActive: child.isActive ?? cat.isActive ?? true, // Inherit isActive from parent
        });
      }
    }
  }

  return result;
};

/**
 * Get all categories with optional filters
 */
const getCategorie = async (filters?: CategoriaFilters): Promise<Categoria[]> => {
  const params = new URLSearchParams();

  if (filters?.tipo) {
    params.append('tipo', filters.tipo);
  }

  const queryString = params.toString();
  const url = queryString ? `/categorie?${queryString}` : '/categorie';

  const response = await api.get<CategoriaBackend[]>(url);
  // Flatten hierarchical response to flat list with parentId
  const flatCategories = flattenCategories(response.data);
  return flatCategories.map(mapBackendToFrontend);
};

/**
 * Get a single category by ID
 */
const getCategoriaById = async (id: number): Promise<Categoria> => {
  const response = await api.get<CategoriaBackend>(`/categorie/${id}`);
  return mapBackendToFrontend(response.data);
};

/**
 * Create a new category
 */
const colorNameToHex: Record<string, string> = {
  Blu: '#2196F3', Verde: '#4CAF50', Rosso: '#F44336',
  Arancione: '#FF9800', Viola: '#9C27B0', Grigio: '#9E9E9E',
};

const normalizeColor = (c?: string): string | undefined => {
  if (!c) return undefined;
  return c.startsWith('#') ? c : colorNameToHex[c] || '#2196F3';
};

const createCategoria = async (data: CategoriaRequest): Promise<Categoria> => {
  const normalizedData = { ...data, colore: normalizeColor(data.colore) };
  const response = await api.post<CategoriaBackend>('/categorie', normalizedData);
  return mapBackendToFrontend(response.data);
};

/**
 * Update an existing category
 */
const updateCategoria = async (id: number, data: CategoriaRequest): Promise<Categoria> => {
  const normalizedData = { ...data, colore: normalizeColor(data.colore) };
  const response = await api.put<CategoriaBackend>(`/categorie/${id}`, normalizedData);
  return mapBackendToFrontend(response.data);
};

/**
 * Delete a category
 */
const deleteCategoria = async (id: number): Promise<void> => {
  await api.delete(`/categorie/${id}`);
};

/**
 * Export Categorie to CSV
 */
const exportCategorie = async (): Promise<void> => {
  const response = await api.get('/categorie/export', {
    responseType: 'blob',
  });

  const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;

  const contentDisposition = response.headers['content-disposition'];
  let filename = 'categorie.csv';
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, '');
    }
  }

  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const categorieService = {
  getCategorie,
  getCategoriaById,
  createCategoria,
  updateCategoria,
  deleteCategoria,
  exportCategorie,
};

export default categorieService;
